import Component from '@glimmer/component';
import { service } from '@ember/service';
import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import { or, and } from 'ember-truth-helpers';
import { variation } from 'ember-launch-darkly';
import can from 'ember-can/helpers/can';
import {
  HdsPageHeader,
  HdsButton,
  HdsDropdown,
} from '@hashicorp/design-system-components/components';
import {
  CoreLayoutMain,
  BreadcrumbsContainer,
  LimitBanner,
  OperationRefreshRoute,
} from 'core/components';
import { resourceTypeFor, resourceIsBusy } from 'core/helpers';
import { TYPE_NETWORK_PEERING } from 'common/utils/cloud-resource-types';
import {
  isAwsProviderNetwork,
  isAzureProviderNetwork,
} from 'networks-common/utils/networks';
import { PEERING_ROUTE_NAMES } from 'networks-common/utils/network-routes';
import Container from 'hcp/components/page/hvns/detail/container';
import Aws from 'hcp/components/page/hvn-peerings/list/aws';
import Azure from 'hcp/components/page/hvn-peerings/list/azure';
import { PEERING_CREATION_TYPE_AUTOMATE } from 'hcp/controllers/cloud/orgs/detail/projects/detail/hvns/detail/peerings/create';

/**
 *
 * `model` will be a model containing the peerings list, the current organization, and project.
 * @argument model;
 * @type {Object}
 *
 */
/**
 *
 * `PageHvnPeeringsList` renders the HVN Peerings List page.
 *
 *
 * ```
 * <Page::HvnPeerings::List
 *   @model={{model}}
 * />
 * ```
 *
 * @class PageHvnPeeringsList
 *
 */
export default class PageHvnPeeringsList extends Component {
  @service analytics;

  PEERING_CREATION_TYPE_AUTOMATE = PEERING_CREATION_TYPE_AUTOMATE;

  get peeringRoutes() {
    return PEERING_ROUTE_NAMES;
  }
  get peeringType() {
    return TYPE_NETWORK_PEERING;
  }
  get isAws() {
    return isAwsProviderNetwork(this.args.model.network);
  }
  get isAzure() {
    return isAzureProviderNetwork(this.args.model.network);
  }

  trackCreateConnectionButtonClick = () => {
    this.analytics.trackEvent(
      'external_ui_hashicorp_network_peering_list_create_connection_button_clicked',
      this.args.model.network
    );
  };

  <template>
    <OperationRefreshRoute
      @route={{this.peeringRoutes.list}}
      @model={{@model.peerings}}
      @resourceType={{resourceTypeFor "network-peering"}}
    />
    <Container @model={{@model}}>
      <CoreLayoutMain>
        <:header>
          <HdsPageHeader as |PH|>
            <PH.Breadcrumb>
              <BreadcrumbsContainer />
            </PH.Breadcrumb>
            <PH.Title>
              {{t "components.page.hvn-peerings.list.title"}}
            </PH.Title>
            {{#unless
              (resourceIsBusy @model.network type=(resourceTypeFor "network"))
            }}
              {{#if (can "create network-peering" @model.peerings)}}
                {{#if
                  (or
                    (and this.isAws (variation "hcp-hvn-guided-flow"))
                    this.isAzure
                  )
                }}
                  <PH.Actions>
                    <HdsButton
                      @color="primary"
                      @icon="plus"
                      @iconPosition="leading"
                      @route={{this.peeringRoutes.create}}
                      @text={{t "components.page.hvn-peerings.create.action"}}
                      data-test-peering-create
                      {{on "click" this.trackCreateConnectionButtonClick}}
                    />
                  </PH.Actions>
                {{else}}
                  <PH.Actions>
                    <HdsDropdown @listPosition="bottom-right" as |dd|>
                      <dd.ToggleButton
                        data-test-peering-create
                        data-test-dropdown-trigger
                        @text={{t "components.page.hvn-peerings.create.action"}}
                      />
                      <dd.Interactive
                        @text={{t
                          "components.page.hvn-peerings.create.automate.title"
                        }}
                        @icon="aws-color"
                        @route={{this.peeringRoutes.create}}
                        @query={{hash
                          peeringCreationType=this.PEERING_CREATION_TYPE_AUTOMATE
                        }}
                        data-test-auto-peering-link
                        data-analytics-consul-onboarding-stepper-auto-peering-link
                      />
                      <dd.Description
                        @text={{t
                          "components.page.hvn-peerings.create.automate.text"
                        }}
                      />
                      <dd.Separator />
                      <dd.Interactive
                        @text={{t
                          "components.page.hvn-peerings.create.manual.title"
                        }}
                        @icon="hashicorp-color"
                        @route={{this.peeringRoutes.create}}
                        @query={{hash peeringCreationType=null}}
                        data-test-manual-peering-link
                        data-analytics-consul-onboarding-stepper-manual-peering-link
                      />
                      <dd.Description
                        @text={{t
                          "components.page.hvn-peerings.create.manual.text"
                        }}
                      />
                    </HdsDropdown>
                  </PH.Actions>
                {{/if}}
              {{/if}}
            {{/unless}}
          </HdsPageHeader>
        </:header>
        <:alert>
          <LimitBanner
            @variant={{this.peeringType}}
            @count={{@model.peerings.length}}
            @padding="0"
          />
        </:alert>
        <:body>
          <div class="hvnsPeeringsList">
            {{#if this.isAws}}
              <Aws @model={{@model}} />
            {{else if this.isAzure}}
              <Azure @model={{@model}} />
            {{/if}}
          </div>
        </:body>
      </CoreLayoutMain>
    </Container>
  </template>
}
